import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {withStyles, makeStyles, useTheme} from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import { getActionLabel, getIcon, statuses } from 'containers/Parking/LocalParking/LocalParkingHosts/Utils';
import { isComponentEnabledForUser, isComponentVisibleForUser, SIP_STATUS } from 'utils';
import HostActionButton from 'containers/Parking/LocalParking/LocalParkingHosts/HostActionButton';
import Edit from '@material-ui/icons/Edit';
import Phone from '@material-ui/icons/Phone';
import { CustomCircleChart, CustomSwitch } from 'components/elements';
import CreateAction from 'components/modals/CreateAction';
import classnames from 'classnames';
import FavoriteIcon from '@material-ui/icons/Favorite';
import CreateCommand from 'components/modals/CreateCommand';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as actions from 'redux/actions/hosts';

const useStyles = makeStyles((theme) => ({
    container: {
        width: 370,
        minHeight: 250,
        margin: theme.spacing(2.5, 3.75, 0.25, 0),
        padding: theme.spacing(2.5),
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        backgroundColor: theme.palette.background.paper,
        borderRadius: 10,
        boxShadow: 'theme.palette.shadow.main',
    },
    flagContainer: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        borderRadius: '10px',
        marginBottom: theme.spacing(2)
    },
    label: {
        marginTop: theme.spacing(2.5),
        color: theme.palette.base[400],
        textTransform: 'uppercase',
    },
    checkboxWrapper: {
        marginTop: theme.spacing(2),
        display: 'flex',
    },
    missedCalls: {
        height: '40px',
        top: '18px',
        left: '365px',
        '& > span': {
            height: '40px',
            width: '40px',
            fontSize: '18px',
            borderRadius: '20px',
        },
    },
    checkboxInner: {
        '& .MuiFormControlLabel-root:first-child': {
            marginBottom: theme.spacing(1),
        },

        '& .MuiIconButton-root': {
            padding: 0,
        },
    },
    optionsWrapper: {
        marginTop: theme.spacing(2),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    connected: {
        color: '#9ECB9B',
        fontSize: '12px',
        backgroundColor:'#D6E7B2',
        borderRadius: '5px',
        padding: '3px',
        width: '105px',
    },
    disconnected: {
        color: '#F9C6BE',
        fontSize: '12px',
        backgroundColor:'#FDE6E2',
        borderRadius: '5px',
        padding: '3px',
        width: '105px',
    },
    statusBad: {
        '& svg': {
            fill: theme.palette.primary.red,
        },
    },
    disconnectedContent: {
        width: '100%',
        minHeight: 150,
        marginTop: theme.spacing(2.5),
        padding: theme.spacing(0, 5),
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.background.default,
        color: theme.palette.base[300],
        textAlign: 'center',
    },
    disconnectedIcon: {
        marginBottom: theme.spacing(1.5),
    },
    zonesWrapper: {
        marginTop: theme.spacing(1),
    },
    averageZone: {
        marginTop: theme.spacing(7),
    },
    zoneLabel: {
        width: 100,
        marginRight: theme.spacing(1),
        color: theme.palette.base[400],
        wordBreak: 'break-all',
    },
    zoneTooltipLabel: {
        width: 200,
        marginRight: theme.spacing(1),
        color: theme.palette.base[400],
        wordBreak: 'break-all',

    },
    zoneStatus: {
        height: 35,
        display: 'flex',
        alignItems: 'center',
    },
    zoneProgressBar: {
        position: 'relative',
        width: '100%',
        height: 18,
        backgroundColor: theme.palette.background.default,
        borderRadius: 9,

        '& .progress-bar': {
            paddingRight: theme.spacing(0.5),
            borderRadius: 9,
            fontSize: 10,
            lineHeight: 1.6,
            color: '#484848',
            textAlign: 'center',
        },
    },
    progressBarRed: {
        '& .progress-bar': {
            backgroundColor: theme.palette.primary.red,
        },
    },
    progressBarGreen: {
        '& .progress-bar': {
            backgroundColor: theme.palette.primary.main,
        },
    },
    progressBarYellow: {
        '& .progress-bar': {
            backgroundColor: theme.palette.primary.yellow,
        },
    },
    error: {
        '& .progress-bar': {
            backgroundColor: 'transparent',
            color: theme.palette.primary.red,
        },
    },
    progressValue: {
        width: '100%',
        position: 'absolute',
    },
    emptyProgressBar: {
        '& .progress-bar': {
            backgroundColor: 'transparent',
        },
    },
    image: {
        height: 30, marginBottom: '-10px', paddingRight: '10px'
    },
    hostActions: {
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
    },    
    hostButton: {
        minWidth: 'calc(50% - 10px)',
        marginBottom: theme.spacing(1),
        '&:last-child': {
            minWidth: '100%',
        },
        borderRadius: '10px',
        boxShadow: 'none'
    },
    hostStatusColumn: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: 13,

        '& .MuiSwitch-root': {
            width: 48,
            paddingRight: 0,
        },
    },
    hostStatusValue: {
        color: theme.palette.base[800],
        textTransform: 'uppercase',
    },
    hostStatusColumnGreen: {
        height: 20,
        width: '100%',
        fontSize: 13,
        display: 'flex',
        flexDirection: 'row',
        marginBottom: '5px',
    },
    hostStatusColumnRed: {
        height: 20,
        width: '100%',
        fontSize: 13,
        display: 'flex',
        flexDirection: 'row',
        marginBottom: '5px',
    },
    hostStatusColumnYellow: {
        height: 20,
        width: '100%',
        fontSize: 13,
        display: 'flex',
        flexDirection: 'row',
        marginBottom: '5px',
    },
    statusColorGreen: {
        width: 5,
        marginRight: '20px',
        backgroundColor: theme.palette.icon.green4,
    },
    statusColorRed: {
        width: 5,
        marginRight: '20px',
        backgroundColor: theme.palette.icon.red4,
    },
    statusColorYellow: {
        width: 5,
        marginRight: '20px',
        backgroundColor: theme.palette.icon.yellow4,
    },
    hostStatusLabel: {
        width: '50%',
        color: theme.palette.base[800],
        textTransform: 'uppercase',
        marginBottom: '5px',
    },
    hostStatusValue2: {
        width: '50%',
        color: theme.palette.base[800],
        textTransform: 'uppercase',
        textAlign: 'right',
    },
    unitName: {
        fontSize:16,
        marginBottom:'15px',
        fontWeight: '500'
    },
    mb: {
        paddingLeft: '10px', paddingRigth: '10px', 
        display: 'flex', flexDirection: 'row', 
        borderBottom: '1px solid #AAAAAA',
        marginBottom: '5px',
    },
    zoneStatusLabel: {
        fontSize: 14,
        opacity: 0.5
    },
    zoneStatusValue: {
        fontSize: 35,
        marginLeft: 'auto',
        paddingRight: '5px',
        marginTop: '-10px',
    },
    error: {
        color: theme.palette.primary.red,
        fontSize: 20,
    },

}));

const HtmlTooltip = withStyles((theme) => ({
    tooltip: {
        backgroundColor: theme.palette.common.white,
        color: 'rgba(0, 0, 0, 0.87)',
        fontSize: theme.typography.pxToRem(12),
        border: '1px solid #dadde9',
        boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)',
        width: '250px',
    },
}))(Tooltip);

function ParkingFavoriteCard({
    parking,
    userUseCases,
    favorite,
    handleCreateTaskOpen,
    sipCall, sipStatus,
    createParkingHostAction,
    createParkingHostCommand,
    hadleHostFavorite,
    handleZoneFavorite
}) {
    const classes = useStyles();
    const {t} = useTranslation();

    const hostStatusTag = (status) => (
        status && status.name
        ? (
        <div
            className={
            status.color==='green' && classes.hostStatusColumnGreen ||
            status.color==='red' && classes.hostStatusColumnRed ||
            status.color==='yellow' && classes.hostStatusColumnYellow
        }
            key={`setting_${status.name}_${status.status}`}
        >
            <div
            className={
                status.color==='green' && classes.statusColorGreen ||
                status.color==='red' && classes.statusColorRed ||
                status.color==='yellow' && classes.statusColorYellow
            }
            />
            <div className={classes.hostStatusLabel}>{t(status.name)}</div>
            <div className={classes.hostStatusValue2}>{status.name==='DisplayText'?favorite.host.displayText:t(status.status)}</div>
        </div>
        )
        : null
    );

    let hostStatus = favorite.host && favorite.host.message && favorite.host.message.split(',');
    hostStatus = hostStatus || [];

    const [openModalAction, setOpenModalAction] = useState(false);
    const [openModalCommand, setOpenModalCommand] = useState(false);
    const [selectedCommand, setSelectedCommand] = useState(null);

    const clickCommandButton = (co) => {
        setSelectedCommand(co);
        setOpenModalCommand(true);
    };

    const getCommand = (commands, action) => {
        let co = commands.filter(item=>item.commandName===action)[0];
        if (!co) return <></>
        return <HostActionButton
            key={`command_${action}`}
            className={classes.hostButton}
            disabled={!isComponentEnabledForUser(`${UC_HOST_COMMAND}${co.commandName}`, userUseCases) || favorite.host.status === 'STATUS_RED'}
            action={co.commandName}
            handleClick={(event) => {
                if (event.screenX && event.screenY && event.screenY !== 0 && event.screenX !== 0) {
                    clickCommandButton(co);
                }
            }}/>
    }

    const [selectedAction, setSelectedAction] = useState(null);
    const clickAction = (co) => () => {
        setSelectedAction(co);
        setOpenModalAction(true);
    };

    const handleCloseAction = () => {
        setOpenModalAction(false);
    };

    const handleCloseCommand = () => {
        setOpenModalCommand(false);
    };

    const [zone, setZone] = useState();
    useEffect(()=>{
        if ( favorite && favorite.zone) {
            setZone({
                zoneId: favorite.zone.zoneId,
                zoneName: favorite.zone.zoneName,
                capacity: favorite.zone.capacity,
                occupancy: favorite.zone.occupancy,
                occupancyReserved: favorite.zone.occupancyReserved,
                reserved: favorite.zone.reserved,
                freeReserved: favorite.zone.capacity === null ? null : (favorite.zone.reserved - favorite.zone.occupancyReserved),
                others: favorite.zone.capacity === null ? null : (favorite.zone.capacity >= favorite.zone.reserved ? (favorite.zone.capacity - favorite.zone.reserved) : 0),
                freeOthers: favorite.zone.capacity === null ? null :(favorite.zone.capacity - favorite.zone.reserved - favorite.zone.occupancy),
                zoneGmtpId: favorite.zone.zoneGmtpId,
                parkingId: favorite.zone.parkingId,
                show: favorite.zone.show,
                progress: favorite.zone.capacity === null ? null : ( favorite.zone.capacity > favorite.zone.occupancy
                    ? Math.floor(((favorite.zone.occupancy + (favorite.zone.occupancyReserved?favorite.zone.occupancyReserved:0)) * 100) / favorite.zone.capacity)
                    : 100),
                validData: favorite.zone.occupancy >= 0 && favorite.zone.capacity >= 0,

            });
        }
    }, [favorite])

    const isReservedValid = ()=> zone.capacity >= zone.reserved; // zone.reserved >= 0 &&
    const isFreeReservedValid = ()=> zone.freeReserved <= zone.reserved; // zone.freeReserved >= 0 &&
    const isFreeOthersValid = ()=> zone.freeOthers <= zone.capacity - zone.reserved; // zone.freeOthers >= 0 &&
    

    const hostSettingsTag = (setting) => {
        return isComponentVisibleForUser(`${UC_HOST_ACTION}${setting.actionName}`, userUseCases) ?
            <div className={classes.hostStatusColumn} key={`setting_${setting.actionName}`}>
                <div className={classes.hostStatusValue}>{getActionLabel(setting.actionName, t)}</div>
                <CustomSwitch
                    disabled={!isComponentEnabledForUser(`${UC_HOST_ACTION}${setting.actionName}`, userUseCases) || favorite.host.status === 'STATUS_RED'}
                    color="primary"
                    checked={setting.actionValue}
                    handleSwitch={clickAction(setting)}
                />
            </div> : null
    };

    const handleFavoriteClick = (favorite) => {
        if ( favorite && favorite.host ) {
            hadleHostFavorite(favorite.host.parkingHostId, false, favorite.type);
        } else if ( favorite && favorite.zone ) {
            handleZoneFavorite(favorite.zone.zoneId, false, favorite.type);
        }
    }

    return (
        <>
            <div className={classes.container}>
            {favorite.type==='FT_FLAG' && favorite.host && favorite.host.message &&
            <div className={classes.flagContainer}>
                <div style={{display: 'flex', flexDirection: 'row', marginLeft: '-7px'}}>
                    <div className={classes.unitName}>
                        <img
                            src={getIcon(favorite.host.iconName)}
                            className={classes.image}
                            alt={favorite.host.hostName}
                            title={favorite.host.hostName}
                        />                        
                        {favorite.host.hostShortName.toUpperCase()}</div>
                    <div style={{marginLeft:'auto', opacity: 0.5, cursor: 'pointer'}} onClick={()=>{handleFavoriteClick(favorite)}}>
                        <FavoriteIcon fontSize='small' style={{color: 'red'}}/>
                    </div>
                </div>

                {hostStatus.map((hs) => {
                    const status = hs && hs.replace('.', '_');
                    return hostStatusTag(statuses[status]);
                })}
            </div>}


            {favorite.type==='FT_COMMAND' && favorite.host && favorite.host.message &&
            <div className={classes.flagContainer}>
                <div style={{display: 'flex', flexDirection: 'row', marginLeft: '-7px'}}>
                    <div className={classes.unitName}>
                        <img
                            src={getIcon(favorite.host.iconName)}
                            className={classes.image}
                            alt={favorite.host.hostName}
                            title={favorite.host.hostName}
                        />                        
                        {favorite.host.hostShortName.toUpperCase()}</div>
                    <div style={{marginLeft:'auto', opacity: 0.5, cursor: 'pointer'}} onClick={()=>{handleFavoriteClick(favorite)}}>
                        <FavoriteIcon fontSize='small' style={{color: 'red'}}/>
                    </div>
                </div>

                <div className={classes.hostActions}>
                    {favorite.host && favorite.host.commands && <>
                        { isComponentVisibleForUser(`${UC_HOST_COMMAND}${'cmd_BtnOpen'}`, userUseCases) && getCommand(favorite.host.commands, 'cmd_BtnOpen')}
                        { isComponentVisibleForUser(`${UC_HOST_COMMAND}${'cmd_BtnDoorOpen'}`, userUseCases) && getCommand(favorite.host.commands, 'cmd_BtnDoorOpen')}
                        { isComponentVisibleForUser(`${UC_HOST_COMMAND}${'cmd_BtnEnumeration'}`, userUseCases) && getCommand(favorite.host.commands, 'cmd_BtnEnumeration')}
                        { isComponentVisibleForUser(`${UC_HOST_COMMAND}${'cmd_BtnClose'}`, userUseCases) && getCommand(favorite.host.commands, 'cmd_BtnClose')}
                        { isComponentVisibleForUser(`${UC_HOST_COMMAND}${'cmd_BtnOpenPerm'}`, userUseCases) && getCommand(favorite.host.commands, 'cmd_BtnOpenPerm')}
                        { isComponentVisibleForUser(`${UC_HOST_COMMAND}${'cmd_BtnSetText'}`, userUseCases) && getCommand(favorite.host.commands, 'cmd_BtnSetText')}
                        { isComponentVisibleForUser(`${UC_HOST_COMMAND}${'cmd_BtnSetFullText'}`, userUseCases) && getCommand(favorite.host.commands, 'cmd_BtnSetFullText')}
                    </>}
                </div>

                <div className={classes.hostActions}>
                    {favorite.host && favorite.host.commands && <>
                        { isComponentVisibleForUser(`${UC_HOST_COMMAND}${'cmd_BtnRestart'}`, userUseCases) && getCommand(favorite.host.commands, 'cmd_BtnRestart')}
                    </>}
                    {favorite.host && isComponentVisibleForUser(UC_TASK_CREATE, userUseCases) && (
                        <HostActionButton
                            className={classes.hostButton}
                            action="createTask2"
                            disabled={!isComponentEnabledForUser(UC_TASK_CREATE, userUseCases)}
                            icon={[<Edit/>]}
                            handleClick={(event) => {
                                if (event.screenX && event.screenY && event.screenY !== 0 && event.screenX !== 0) {
                                    handleCreateTaskOpen({
                                        parkingId: favorite.host.parkingId,
                                        parkingHostId: favorite.host.parkingHostId,
                                        parkingHost: favorite.host.hostName,
                                    });
                                }
                            }}
                        />)}
                    {favorite.host && isComponentVisibleForUser(UC_HOST_CALL, userUseCases) && (
                        <HostActionButton
                            key="command_call"
                            className={classes.hostButton}
                            disabled={!favorite.host.sipPhoneNumber || !isComponentEnabledForUser(UC_HOST_CALL, userUseCases) || sipStatus!==SIP_STATUS.CONNECTED}
                            action="callIntercom"
                            icon={[<Phone/>]}
                            handleClick={() => {
                                sipCall('call-audio', favorite.host.sipPhoneNumber);
                            }}
                        />
                    )}
                </div>            

                {openModalCommand && (
                    <CreateCommand
                        opened={openModalCommand}
                        command={selectedCommand}
                        host={favorite.host}
                        createParkingHostCommand={createParkingHostCommand}
                        handleClose={handleCloseCommand}
                    />
                )}
            </div>}

            {favorite.type==='FT_ACTION' && favorite.host && favorite.host.message &&
            <div className={classes.flagContainer}>
                <div style={{display: 'flex', flexDirection: 'row', marginLeft: '-7px'}}>
                    <div className={classes.unitName}>
                        <img
                            src={getIcon(favorite.host.iconName)}
                            className={classes.image}
                            alt={favorite.host.hostName}
                            title={favorite.host.hostName}
                        />                        
                        {favorite.host.hostShortName.toUpperCase()}</div>
                    <div style={{marginLeft:'auto', opacity: 0.5, cursor: 'pointer'}} onClick={()=>{handleFavoriteClick(favorite)}}>
                        <FavoriteIcon fontSize='small' style={{color: 'red'}}/>
                    </div>
                </div>

                <div className={classes.hostStatus}>
                    {favorite.host && favorite.host.actions && favorite.host.actions.map((hs) => hostSettingsTag(hs))}
                </div>

                {openModalAction && (
                    <CreateAction
                        action={selectedAction}
                        host={favorite.host}
                        opened={openModalAction}
                        handleClose={handleCloseAction}
                        createParkingHostAction={createParkingHostAction}
                    />
                )}
            </div>}

            {favorite.type==='FT_ZONE_BAR' && zone && 
            <div className={classes.flagContainer}>
                <div style={{display: 'flex', flexDirection: 'row', marginLeft: '-7px'}}>
                    <div className={classes.unitName}>{zone.zoneName.toUpperCase()}</div>
                    <div style={{marginLeft:'auto', opacity: 0.5, cursor: 'pointer'}} onClick={()=>{handleFavoriteClick(favorite)}}>
                        <FavoriteIcon fontSize='small' style={{color: 'red'}}/>
                    </div>
                </div>
                <div>{t('OCCUPANCY').toUpperCase()}</div>
                <CustomCircleChart
                    height={250}
                    series={[zone.progress!==null?(zone.progress>100?100:zone.progress):'-']}
                    label=''
                />
            </div>}

            {favorite.type==='FT_ZONE_INFO' && zone &&
            <div className={classes.flagContainer}>
                <div style={{display: 'flex', flexDirection: 'row', marginLeft: '-7px'}}>
                    <div className={classes.unitName}>{zone.zoneName.toUpperCase()}</div>
                    <div style={{marginLeft:'auto', opacity: 0.5, cursor: 'pointer'}} onClick={()=>{handleFavoriteClick(favorite)}}>
                        <FavoriteIcon fontSize='small' style={{color: 'red'}}/>
                    </div>
                </div>
                <div className={classes.mb}>
                    <div className={classes.zoneStatusLabel}>{t('CAPACITY').toUpperCase()}</div>
                    <div className={classnames(classes.zoneStatusValue, !zone.validData && classes.error)}>
                    {zone.validData ? (zone.capacity!==null?zone.capacity:'-') : t('INVALID_DATA')}
                    </div>
                </div>

                <div className={classes.line}></div>

                <div className={classes.mb} >
                    <div className={classes.zoneStatusLabel}>{t('RESERVED').toUpperCase()}</div>
                    <div className={classnames(classes.zoneStatusValue, !isReservedValid && classes.error)}>
                    {isReservedValid ? (zone.reserved!==null?zone.reserved:'-') : t('INVALID_DATA')}
                    </div>
                </div>

                <div className={classes.line}></div>

                <div className={classes.mb}>
                    <div className={classes.zoneStatusLabel}>{t('FREE_RESERVED').toUpperCase()}</div>
                    <div className={classnames(classes.zoneStatusValue, !isFreeReservedValid && classes.error)} >
                    {isFreeReservedValid ? (zone.freeReserved!==null?zone.freeReserved:'-') : t('INVALID_DATA')}
                    </div>
                </div>

                <div className={classes.line}></div>

                <div className={classes.mb}>
                    <div className={classes.zoneStatusLabel}>{t('OTHERS').toUpperCase()}</div>
                    <div className={classnames(classes.zoneStatusValue, !isReservedValid && classes.error)}>
                    {isReservedValid ? (zone.others!==null?zone.others:'-') : t('INVALID_DATA')}
                    </div>
                </div>

                <div className={classes.line}></div>

                <div className={classes.mb}>
                    <div className={classes.zoneStatusLabel}>{t('FREE_OTHERS').toUpperCase()}</div>
                    <div className={classnames(classes.zoneStatusValue, !isFreeOthersValid && classes.error)}>
                    {isFreeOthersValid ? (zone.freeOthers!==null?zone.freeOthers:'-') : t('INVALID_DATA')}
                    </div>
                </div>
            </div>}

            </div>
        </>
    );
}

ParkingFavoriteCard.propTypes = {
        parking: PropTypes.object,
        handleClick: PropTypes.func,
        sipToggle: PropTypes.object,
};

ParkingFavoriteCard.defaultProps = {
        parking: {},
        handleClick: undefined,
};

const mapStateToProps = (store) => ({
  isCreatingCommand: store.hostsData.isCreatingCommand,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  createParkingHostCommand: actions.createParkingHostCommand,
  createParkingHostAction: actions.createParkingHostAction,
}, dispatch);

const UC_HOST_CALL = 'UC0010';
const UC_HOST_COMMAND = 'UC0011_';
const UC_TASK_CREATE = 'UC0038';
const UC_HOST_ACTION = 'UC0012_';

// export default ParkingFavoriteCard;

export default React.memo(connect(
    mapStateToProps,
    mapDispatchToProps,
)(ParkingFavoriteCard), (o1, o2)=>{return o2.active===false});
